import React, { useState } from 'react';
import StepProgress from '../common/StepProgress';
import { createExamProject } from '../../api/examApi';

const EXAM_TYPES = [
  { value: 'midterm',       label: 'Mid-Term Exam' },
  { value: 'end_semester',  label: 'End-Semester Exam' },
  { value: 'internal',      label: 'Internal Assessment' },
  { value: 'quiz',          label: 'Quiz / Surprise Test' },
  { value: 'assignment',    label: 'Assignment' },
];

const ASSESSMENT_TYPES = [
  { value: 'theory',    label: 'Theory' },
  { value: 'practical', label: 'Practical / Lab' },
  { value: 'viva',      label: 'Viva Voce' },
  { value: 'mixed',     label: 'Mixed' },
];

const TAXONOMY_FRAMEWORKS = [
  { value: 'blooms_revised', label: "Bloom's Revised Taxonomy" },
  { value: 'blooms_original', label: "Bloom's Original Taxonomy" },
];

const PROJECT_TYPES = [
  {
    value: 'standard',
    label: 'AI-Generated Q.Bank',
    description: 'Upload curriculum → AI extracts COs/LOs → CKB → Blueprint → AI generates questions',
  },
  {
    value: 'qbank_upload',
    label: 'Upload Existing Q.Bank',
    description: 'Upload curriculum → Upload your own Q.Bank (DOCX/XLSX/PDF) → AI maps COs/LOs → Create Q.Sets',
  },
];

const STEPS = ['Project Type', 'Basic Info', 'Exam Config', 'Review & Create'];

const EMPTY_FORM = {
  project_type: 'standard',
  title: '',
  institution_name: '',
  course_name: '',
  course_code: '',
  semester: '',
  batch: '',
  regulation: '',
  exam_type: 'midterm',
  assessment_type: 'theory',
  language: 'English',
  outcome_framework: 'CO/PO/PSO',
  taxonomy_framework: 'blooms_revised',
};

export default function NewProjectWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!form.project_type;
    if (step === 1) return form.title.trim().length > 0 && form.course_name.trim().length > 0;
    return true;
  };

  const handleCreate = async () => {
    setLoading(true);
    setError('');
    try {
      const project = await createExamProject(form);
      onCreated?.(project);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="exam-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="exam-modal exam-modal-lg">
        <div className="exam-modal-header">
          <h2 className="exam-modal-title">New Examination Project</h2>
          <button className="exam-modal-close" onClick={onClose}>
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="14" height="14">
              <line x1="1" y1="1" x2="13" y2="13"/>
              <line x1="13" y1="1" x2="1" y2="13"/>
            </svg>
          </button>
        </div>

        <div className="exam-modal-body">
          <StepProgress steps={STEPS} current={step} />

          {/* Step 0 — Project Type */}
          {step === 0 && (
            <div>
              <p style={{ fontSize: 13, color: 'var(--exam-text-secondary)', marginBottom: 16 }}>
                Choose how questions will be generated for this project.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {PROJECT_TYPES.map((pt) => (
                  <label
                    key={pt.value}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 14,
                      padding: '14px 16px', borderRadius: 'var(--exam-radius)',
                      border: `2px solid ${form.project_type === pt.value ? 'var(--exam-primary)' : 'var(--exam-border)'}`,
                      background: form.project_type === pt.value ? 'var(--exam-primary-light)' : 'var(--exam-surface)',
                      cursor: 'pointer', transition: 'all var(--exam-transition)',
                    }}
                  >
                    <input
                      type="radio"
                      name="project_type"
                      value={pt.value}
                      checked={form.project_type === pt.value}
                      onChange={() => set('project_type', pt.value)}
                      style={{ marginTop: 3 }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: 'var(--exam-text)' }}>
                        {pt.label}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--exam-text-secondary)', lineHeight: 1.5 }}>
                        {pt.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 1 — Basic Info */}
          {step === 1 && (
            <div>
              <div className="exam-field">
                <label className="exam-label required">Project Title</label>
                <input
                  className="exam-input"
                  placeholder="e.g. B.Tech CSE — Operating Systems — Nov 2026"
                  value={form.title}
                  onChange={(e) => set('title', e.target.value)}
                  autoFocus
                />
              </div>
              <div className="exam-grid-2">
                <div className="exam-field">
                  <label className="exam-label required">Course Name</label>
                  <input
                    className="exam-input"
                    placeholder="Operating Systems"
                    value={form.course_name}
                    onChange={(e) => set('course_name', e.target.value)}
                  />
                </div>
                <div className="exam-field">
                  <label className="exam-label">Course Code</label>
                  <input
                    className="exam-input"
                    placeholder="CS3401"
                    value={form.course_code}
                    onChange={(e) => set('course_code', e.target.value)}
                  />
                </div>
              </div>
              <div className="exam-grid-2">
                <div className="exam-field">
                  <label className="exam-label">Semester / Year</label>
                  <input
                    className="exam-input"
                    placeholder="Semester 5 / 2026"
                    value={form.semester}
                    onChange={(e) => set('semester', e.target.value)}
                  />
                </div>
                <div className="exam-field">
                  <label className="exam-label">Batch / Regulation</label>
                  <input
                    className="exam-input"
                    placeholder="2023 Batch / R2021"
                    value={form.batch}
                    onChange={(e) => set('batch', e.target.value)}
                  />
                </div>
              </div>
              <div className="exam-field">
                <label className="exam-label">Institution</label>
                <input
                  className="exam-input"
                  placeholder="Your institution name (auto-filled if configured)"
                  value={form.institution_name}
                  onChange={(e) => set('institution_name', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 2 — Exam Config */}
          {step === 2 && (
            <div>
              <div className="exam-grid-2">
                <div className="exam-field">
                  <label className="exam-label">Exam Type</label>
                  <select
                    className="exam-select"
                    value={form.exam_type}
                    onChange={(e) => set('exam_type', e.target.value)}
                  >
                    {EXAM_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="exam-field">
                  <label className="exam-label">Assessment Type</label>
                  <select
                    className="exam-select"
                    value={form.assessment_type}
                    onChange={(e) => set('assessment_type', e.target.value)}
                  >
                    {ASSESSMENT_TYPES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="exam-grid-2">
                <div className="exam-field">
                  <label className="exam-label">Outcome Framework</label>
                  <select
                    className="exam-select"
                    value={form.outcome_framework}
                    onChange={(e) => set('outcome_framework', e.target.value)}
                  >
                    <option value="CO/PO/PSO">CO / PO / PSO</option>
                    <option value="LO/CO">LO / CO (simplified)</option>
                    <option value="CO">CO only</option>
                  </select>
                </div>
                <div className="exam-field">
                  <label className="exam-label">Taxonomy Framework</label>
                  <select
                    className="exam-select"
                    value={form.taxonomy_framework}
                    onChange={(e) => set('taxonomy_framework', e.target.value)}
                  >
                    {TAXONOMY_FRAMEWORKS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="exam-field">
                <label className="exam-label">Language of Assessment</label>
                <select
                  className="exam-select"
                  value={form.language}
                  onChange={(e) => set('language', e.target.value)}
                >
                  <option value="English">English</option>
                  <option value="Tamil">Tamil</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Bilingual">Bilingual (English + Regional)</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 3 — Review */}
          {step === 3 && (
            <div>
              <div className="exam-card" style={{ background: 'var(--exam-bg)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {[
                      ['Project Type', PROJECT_TYPES.find(t => t.value === form.project_type)?.label],
                      ['Project Title', form.title],
                      ['Course', `${form.course_name}${form.course_code ? ` (${form.course_code})` : ''}`],
                      ['Semester / Batch', [form.semester, form.batch].filter(Boolean).join(' · ') || '—'],
                      ['Institution', form.institution_name || '—'],
                      ['Exam Type', EXAM_TYPES.find(t => t.value === form.exam_type)?.label],
                      ['Assessment Type', ASSESSMENT_TYPES.find(t => t.value === form.assessment_type)?.label],
                      ['Outcome Framework', form.outcome_framework],
                      ['Taxonomy', TAXONOMY_FRAMEWORKS.find(t => t.value === form.taxonomy_framework)?.label],
                      ['Language', form.language],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding: '7px 0', color: 'var(--exam-text-muted)', width: 160, fontWeight: 500 }}>{k}</td>
                        <td style={{ padding: '7px 0', color: 'var(--exam-text)', fontWeight: 500 }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {error && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--exam-error-light)', color: 'var(--exam-error)', borderRadius: 'var(--exam-radius-sm)', fontSize: 13 }}>
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="exam-modal-footer">
          {step > 0 && (
            <button className="exam-btn exam-btn-ghost exam-btn-md" onClick={() => setStep(s => s - 1)}>
              Back
            </button>
          )}
          <button className="exam-btn exam-btn-ghost exam-btn-md" onClick={onClose}>
            Cancel
          </button>
          {step < STEPS.length - 1 ? (
            <button
              className="exam-btn exam-btn-primary exam-btn-md"
              onClick={() => setStep(s => s + 1)}
              disabled={!canNext()}
            >
              Continue
            </button>
          ) : (
            <button
              className="exam-btn exam-btn-primary exam-btn-md"
              onClick={handleCreate}
              disabled={loading}
            >
              {loading ? <span className="exam-spinner" style={{ width: 14, height: 14 }} /> : null}
              {loading ? 'Creating…' : 'Create Project'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
